import {AutoInitializing, UnLitElement, WithEopSlots} from "../../../common/elements";
import {customElement, property} from "lit/decorators.js";
import {ManagingResources} from "../../../common/lifetime";
import {SearchFacade} from "./searchFacade";
import {resolve} from "../../../container";
import {ScrollableBar} from "../../../page/elements/scrollableBar";
import {Dictionary} from "../../../page/elements/dictionary";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {ResizeObserverFactory} from "../../../common/observation";
import {DefaultSearchGroup} from "./searchGroup";
import {QueryParameters} from "../../../common/queryParameters";
import {TAB_QUERY_PARAMETER_NAME} from "./constants";
import type {PropertyMap} from "../../../common/utils/objects";
import type {FeedChannel} from "../feedCommons";

export type GroupConfiguration = {
    name: string;
    channels: PropertyMap<ChannelConfiguration>;
};

export type ChannelConfiguration = {
    width?: FeedChannel["width"];
};

@customElement("eop-search-group-tabs")
export class EopSearchGroupTabs extends WithEopSlots(ManagingResources(AutoInitializing(UnLitElement))) {

    @property({attribute: "groups-configuration", type: Array})
    public groupsConfigurations: GroupConfiguration[] = [];

    private focusResizeObserver: ResizeObserver;
    private scrollBar: ScrollableBar;
    private dictionary: Dictionary;

    private groups: DefaultSearchGroup[] = [];

    public constructor(
        private searchFacade: SearchFacade = resolve(SearchFacade),
        resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private queryParameters: QueryParameters = resolve(QueryParameters)
    ) {
        super();
        this.focusResizeObserver = resizeObserverFactory.create(() => this.searchFacade.focus());
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.focusResizeObserver.observe(this);

        const groupNameFromQueryStr = this.queryParameters.getString(TAB_QUERY_PARAMETER_NAME);
        this.groups = this.groupsConfigurations.map(groupConfiguration => {
            const sourcesProperties: PropertyMap<FeedChannel> = {};
            for (const channel of Object.keys(groupConfiguration.channels)) {
                sourcesProperties[channel] = groupConfiguration.channels[channel];
            }
            return new DefaultSearchGroup(groupConfiguration.name, groupConfiguration.name === groupNameFromQueryStr, sourcesProperties);
        });
    }

    public disconnectedCallback(): void {
        this.focusResizeObserver.disconnect();
        this.scrollBar.disconnect();
        super.disconnectedCallback();
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.scrollBar = new ScrollableBar(this.renderRoot.querySelector(".content-search-tabs")!, this.dictionary);
        this.searchFacade.registerGroups(this.groups);
    }

    public render(): TemplateResult {
        return html`
            <div class="content-search-tabs-container">
                <nav class="content-search-tabs">
                    <div class="content-search-tab-items horizontally-scrollable-area">
                        ${this.renderSearchGroups()}
                    </div>
                </nav>
            </div>
        `;
    }

    private renderSearchGroups(): TemplateResult[] {
        return this.groups.map(group => html`
            <div class="content-search-tab-item">
                <eop-search-group-tab .group=${group}></eop-search-group-tab>
            </div>
        `);
    }
}
